// ─────────────────────────────────────────────────────────────
// useMaintenance — site-wide maintenance mode state machine.
//
// Owns:
//   • The current maintenance state ({enabled, message, updatedAt, adminBypass})
//   • Initial fetch on mount (parallel to the rest of app boot — never blocks)
//   • Hard-kick: registers the maintenance handler with the api client so
//     a 503-with-maintenance from any in-flight request flips us into
//     maintenance mode immediately.
//   • A 30-second poll that auto-clears the state when the backend reports
//     `enabled: false`, so users on the maintenance screen recover without
//     a manual refresh.
//
// Used by ScriptHelperApp (covers /, /discoverstudio) and DashboardApp
// (covers /dashboard). Each consumer decides what to render based on its
// own knowledge of whether the current user is an admin.
// ─────────────────────────────────────────────────────────────

import { useCallback, useEffect, useRef, useState } from "react";
import { maintenanceApi, setMaintenanceHandler } from "../api/client";
import type { MaintenanceErrorPayload } from "../api/client";

export interface MaintenanceState {
  enabled: boolean;
  message: string | null;
  updatedAt: string | null;
  /**
   * Set to `true` when an admin (or aspiring admin) clicks
   * "Admin? Sign in here" on the maintenance screen — this lets the
   * normal login flow run even though the screen would otherwise
   * cover everything.
   */
  adminBypass: boolean;
}

const POLL_MS = 30_000;

export interface UseMaintenanceResult {
  state: MaintenanceState | null;
  /** True until the initial GET /api/system/maintenance returns. */
  loading: boolean;
  setAdminBypass: (bypass: boolean) => void;
  /** Replace the state outright (used by the admin banner toggle + post-save refresh). */
  setState: (next: MaintenanceState | null) => void;
  /** Re-fetch the status now (e.g. after the admin saves a toggle). */
  refresh: () => Promise<void>;
}

export function useMaintenance(): UseMaintenanceResult {
  const [state, setStateInternal] = useState<MaintenanceState | null>(null);
  const [loading, setLoading] = useState(true);
  // Ref mirror so callbacks don't get stale state via closure.
  const stateRef = useRef<MaintenanceState | null>(null);
  stateRef.current = state;

  const applyPayload = useCallback(
    (payload: { enabled: boolean; message: string | null; updated_at: string | null }) => {
      if (!payload.enabled) {
        // Clear when the server reports the gate is off.
        setStateInternal(null);
        return;
      }
      // Preserve adminBypass across refreshes — only the user clicking the
      // "Admin? Sign in here" link should flip it.
      const prev = stateRef.current;
      setStateInternal({
        enabled: true,
        message: payload.message,
        updatedAt: payload.updated_at,
        adminBypass: prev?.adminBypass ?? false,
      });
    },
    []
  );

  const refresh = useCallback(async () => {
    try {
      const status = await maintenanceApi.status();
      applyPayload(status);
    } catch {
      // Network failure on the status check is non-fatal — leave state as-is.
    }
  }, [applyPayload]);

  // ── Initial fetch (parallel to the rest of app boot) ────────
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const status = await maintenanceApi.status();
        if (cancelled) return;
        applyPayload(status);
      } catch {
        // Silently ignore — the rest of the app boots normally.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [applyPayload]);

  // ── Hard-kick: register the global 503 handler ──────────────
  useEffect(() => {
    const handler = (payload: MaintenanceErrorPayload) => {
      const prev = stateRef.current;
      setStateInternal({
        enabled: true,
        message: payload.message,
        updatedAt: payload.updated_at,
        // If the user had clicked "Admin? Sign in here", keep the bypass —
        // they may be mid-login. Otherwise default to false.
        adminBypass: prev?.adminBypass ?? false,
      });
    };
    setMaintenanceHandler(handler);
    return () => setMaintenanceHandler(null);
  }, []);

  // ── 30s poll while maintenance is active ────────────────────
  useEffect(() => {
    if (!state?.enabled) return;
    const interval = window.setInterval(() => {
      void refresh();
    }, POLL_MS);
    return () => window.clearInterval(interval);
  }, [state?.enabled, refresh]);

  const setAdminBypass = useCallback((bypass: boolean) => {
    setStateInternal((prev) => (prev ? { ...prev, adminBypass: bypass } : prev));
  }, []);

  const setState = useCallback((next: MaintenanceState | null) => {
    setStateInternal(next);
  }, []);

  return { state, loading, setAdminBypass, setState, refresh };
}
